import React from 'react';
import ReportIssueButton from '../../components/ReportIssue';
import SendPointer from '../../components/SendPointer';
import ActivityPills from '../../components/ActivityPills';

// ─────────────────────────────────────────────────────────────────────────────
// The dark job banner: number, name, status, meta, who correspondence goes to,
// and the row of job actions.
//
// It was inline in AdminBidDetail. It's shared now because the rebuilt five-tile
// page needs the same banner above the tiles — and two hand-copied banners would
// drift the first time one of them changed.
//
// Actions come in as callbacks: the old page opens its own modals, the new page
// opens its own or sends you across. The banner itself doesn't know the difference.
// ─────────────────────────────────────────────────────────────────────────────

const SCHEDULE_BOARDS = [
  ['small_jobs', 'Small Jobs board'],
  ['vertical_drilling', 'Vertical Drilling'],
  ['horizontal_drilling', 'Directional Drilling'],
  ['loops', 'Loops'],
];

const ghost = {
  padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.4)',
  background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
};

// Who documents off this job are addressed to. The app already worked this out for
// submittals but only said so inside that one tab, so everywhere else you were left
// inferring it from the owner and the bid recipients — neither of which is the
// correspondent. Shared so the banner and the submittals tab can't disagree.
export function computeAddressee(bid, recipients = [], project = null) {
  if (!bid) return null;
  if ((bid.job_type || '') === 'Sub-Contractor') {
    // The JOB's contact comes first. A bid goes to whoever picked it up — an
    // estimator, a front desk — and that isn't necessarily who runs the job. The
    // General Contact on the Job Information tile is the one deliberately set for
    // this job, so it outranks the bid recipient rather than the other way round.
    if (project?.awarded_by_name || project?.awarded_by_company) {
      const company = project.awarded_by_company || '';
      const to = project.awarded_by_name || company;
      return { to, email: project.awarded_by_email || '', role: 'prime',
        display: company && to !== company ? `${to} (${company})` : to };
    }
    const awarded = recipients.find(r => r.low_bidder) || null;
    if (awarded && (awarded.contact_name || awarded.client_name)) {
      const to = awarded.contact_name || awarded.client_name;
      const company = awarded.client_name || '';
      return { to, email: awarded.contact_email || '', role: 'prime',
        display: company && to !== company ? `${to} (${company})` : to };
    }
    return null;
  }
  const to = bid.client_contact || bid.client_name || '';
  if (!to) return null;
  return { to, email: bid.client_email || '', role: 'owner',
    display: bid.client_name && to !== bid.client_name ? `${to} (${bid.client_name})` : to };
}

export default function JobBanner({
  project, bid, projectId, statusMeta, addressee, currentUserRole, isLocked,
  onBack, onEditProject, onDelete, onWritePO, onQuotePdf, onLocation, onScheduleWorkflow, onStatusChange,
  onTakeToCard,
  shareable = false,
}) {
  if (!project || !bid) return null;
  const canDelete = ['admin', 'Admin', 'Executive'].includes(currentUserRole);
  // Active / Complete / Closed — the job's own status, set here by the office.
  const canSetStatus = !!onStatusChange && ['admin', 'Admin', 'Executive', 'ProjectManager', 'Accounting', 'OfficeStaff'].includes(currentUserRole);
  const JOB_STATUS = [['active', 'Active'], ['complete', 'Complete'], ['closed', 'Closed']];
  const statusWord = (JOB_STATUS.find(([k]) => k === project.status) || [null, project.status])[1];

  return (
    <div style={{ background: '#1e3a5f', color: '#fff', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
      {/* Title row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
          <button onClick={onBack}
            style={{ background: 'none', border: 'none', color: '#fff', fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: 0, opacity: 0.85, marginTop: 1 }}>‹</button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 28, fontWeight: 800, fontFamily: 'monospace', letterSpacing: 0.5, lineHeight: 1.05 }}>{project.project_number || 'Project'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
              <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{bid.job_name || project.name || 'Job'}</h1>
              {statusMeta && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: statusMeta.color + '33', color: '#fff', border: `1px solid ${statusMeta.color}` }}>{statusMeta.label}</span>
              )}
            </div>
            <div style={{ fontSize: 12.5, opacity: 0.85, marginTop: 5 }}>
              {project.division === 'small' ? 'Work Order' : 'Large Job'}
              {' · '}{canSetStatus ? (
                <select value={JOB_STATUS.some(([k]) => k === project.status) ? project.status : 'active'}
                  onChange={e => onStatusChange(e.target.value)} title="The job's status — Complete and Closed jobs drop off the active lists"
                  style={{ fontSize: 12, fontWeight: 700, padding: '2px 6px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.45)',
                    background: project.status === 'closed' ? 'rgba(107,114,128,0.6)' : project.status === 'complete' ? 'rgba(22,163,74,0.75)' : 'rgba(255,255,255,0.14)',
                    color: '#fff', cursor: 'pointer' }}>
                  {JOB_STATUS.map(([k, l]) => <option key={k} value={k} style={{ color: '#111' }}>{l}</option>)}
                </select>
              ) : <span style={{ textTransform: 'capitalize' }}>{statusWord}</span>}
              {project.sched_start && <> · 📅 {(d => new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }))(project.sched_start)}{project.sched_finish && project.sched_finish !== project.sched_start ? <> → {(d => new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }))(project.sched_finish)}</> : null}</>}
              {bid.client_name && <> · {bid.client_name}</>}
            </div>
            {bid.job_location && <div style={{ fontSize: 12.5, opacity: 0.8, marginTop: 2 }}>📍 {bid.job_location}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8, padding: '5px 11px', borderRadius: 999,
              background: addressee ? 'rgba(255,255,255,0.14)' : 'rgba(234,88,12,0.9)',
              border: `1px solid ${addressee ? 'rgba(255,255,255,0.3)' : '#f97316'}`, alignSelf: 'flex-start' }}>
              <span style={{ fontSize: 12 }}>{addressee ? '✉️' : '⚠️'}</span>
              {addressee ? (
                <span style={{ fontSize: 12.5 }}>
                  Correspondence → <strong>{addressee.display}</strong>
                  <span style={{ opacity: 0.75 }}> · the {addressee.role === 'prime' ? 'general contractor' : 'owner'}</span>
                </span>
              ) : (
                <span style={{ fontSize: 12.5, fontWeight: 700 }}>
                  No one to address documents to — mark the awarded contractor on Bid Recipients
                </span>
              )}
            </div>
            {project.division === 'small' && (onScheduleWorkflow || onTakeToCard) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 9, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11.5, opacity: 0.85 }}>🗂️ Field schedule tracked on:</span>
                {onScheduleWorkflow ? (
                  <select value={project.schedule_workflow || 'small_jobs'} onChange={e => onScheduleWorkflow(e.target.value)}
                    title="Which scheduled board tracks this work order's field schedule"
                    style={{ fontSize: 12, fontWeight: 700, padding: '4px 8px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.18)', color: '#fff', cursor: 'pointer' }}>
                    {SCHEDULE_BOARDS.map(([v, l]) => (
                      <option key={v} value={v} style={{ color: '#111', background: '#fff' }}>{l}</option>
                    ))}
                  </select>
                ) : <span style={{ fontSize: 12, fontWeight: 700 }}>{(SCHEDULE_BOARDS.find(([v]) => v === (project.schedule_workflow || 'small_jobs')) || [null, 'Small Jobs board'])[1]}</span>}
                {/* Searching a job lands here, not on the card where the field work is tracked
                    (Ryan, Nov 3 2026) — this takes you to that card on its board. */}
                {onTakeToCard && (
                  <button onClick={onTakeToCard} title="Open this work order's card on that board — where the field schedule, updates and photos are tracked"
                    style={{ ...ghost, padding: '4px 12px', fontSize: 12, background: 'rgba(232,119,34,0.9)', border: '1px solid #f59e0b' }}>
                    ➜ Take to Card
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'flex-start' }}>
          {/* Send the whole job to someone. Cards and pillows carry their own. */}
          {shareable && (
            <SendPointer tone="dark"
              path={`/admin/jobs/${projectId}`}
              label={(String(project.name || '').startsWith(String(project.project_number || ''))
                ? String(project.name || '')
                : `${project.project_number || ''} ${project.name || ''}`.trim()).slice(0, 60)} />
          )}
          {/* In place of Edit Project (Pat, Sep 24 2026): a way to tell the office
              something in the app is wrong, or ask for something, from wherever they are. */}
          <ReportIssueButton style={ghost} />
          {canDelete && onDelete && (
            <button onClick={onDelete}
              style={{ ...ghost, border: '1px solid rgba(255,120,120,0.5)', background: 'rgba(255,0,0,0.15)', color: '#fca5a5' }}>
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Pills + actions row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
        <ActivityPills variant="onDark" pageName="Job" scope="job" scopeId={projectId} summaryLabel="Job Summary" />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {onWritePO && (
            <button onClick={onWritePO}
              style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              🧾 Write PO
            </button>
          )}
          {onQuotePdf && (
            <button onClick={onQuotePdf}
              style={{ ...ghost, fontSize: 13, fontWeight: 600, padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
              📄 {isLocked ? 'View Used Quote PDF' : 'Export Quote PDF'}
            </button>
          )}
          {onLocation && (
            <button onClick={onLocation}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', fontSize: 12.5, fontWeight: 700, borderRadius: 999, padding: '6px 14px', cursor: 'pointer' }}>
              📍 Location
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
